import {Component, computed, effect, inject, input, output, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {Card} from 'primeng/card';
import {TranslocoService} from '@jsverse/transloco';
import {DirectusService, DirectusTranslationPipe, LanguageCode} from '@frontend/directus';
import {CardTitleComponent} from '../../../../../../../shared/components/header/card-title/card-title.component';
import {ModelFieldSelectComponent} from '../../../../../../../shared/components/inputs/model-field/model-field-select/model-field-select.component';
import {ProjectStepSaveButtonComponent} from '../../../../../../../shared/project-step-save-button/project-step-save-button.component';
import {LanguageOption} from './step-1-3-project-language-tab.types';
@Component({
  selector: 'app-project-language-tab',
  imports: [Card, CardTitleComponent, ModelFieldSelectComponent, ProjectStepSaveButtonComponent],
  templateUrl: './step-1-3-project-language-tab.component.html',
})
export class Step13ProjectLanguageTabComponent {
  private readonly directusService = inject(DirectusService);
  private readonly translocoService = inject(TranslocoService);
  private readonly directusTranslationPipe = new DirectusTranslationPipe();

  readonly originalLanguage = input<string | null>(null);
  readonly isSaving = input(false);
  readonly disabled = input<boolean>(false);

  readonly submitOutput = output<{projectLanguage: string}>();

  readonly selectedLanguage = signal<string | null>(null);

  private readonly languageCodes = toSignal(this.directusService.loadLanguageCodes(), {
    initialValue: [],
  });

  private readonly activeLanguage = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  readonly languageOptions = computed<LanguageOption[]>(() => {
    const activeLanguage: string = this.activeLanguage();
    const options: LanguageOption[] = this.languageCodes().map((language) =>
      this.toLanguageOption(language, activeLanguage),
    );
    return options.sort((first, second) => first.label.localeCompare(second.label));
  });

  readonly hasChanges = computed<boolean>(
    () => this.originalLanguage() !== this.selectedLanguage(),
  );

  readonly shouldShowSaveButton = computed<boolean>(
    () => this.hasChanges() && this.selectedLanguage() !== null,
  );

  constructor() {
    effect(() => {
      const original: string | null = this.originalLanguage();
      this.selectedLanguage.set(original);
    });
  }

  onSubmit(): void {
    const projectLanguage: string | null = this.selectedLanguage();
    if (projectLanguage === null) {
      throw new Error('No project language selected');
    }
    this.submitOutput.emit({projectLanguage: projectLanguage});
  }

  private toLanguageOption(language: LanguageCode, activeLanguage: string): LanguageOption {
    const label: string =
      this.directusTranslationPipe.transform(language.translations, activeLanguage) ??
      language.isoCode;
    return {isoCode: language.isoCode, label: label};
  }
}
